import React from 'react';
import StepNavigation from './StepNavigation';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { findIndex, capitalize, toLower, snakeCase, uniq } from 'lodash';
import CheckButton from '../CheckButton/CheckButton';
import PropTypes from 'prop-types';

// Import OnboardingContext
import { useOnboardingState } from '../../contexts/OnboardingContext';

// GraphQL mutation for updating user data
const UPDATE_CURRENT_USER = gql`
  mutation updateCurrentUser($primary_markets: [String!], $designations: [String!], $advisor_licenses: [String!], $tag_list: [String!], $locale: String) {
    updateCurrentUser(primary_markets: $primary_markets, designations: $designations, advisor_licenses: $advisor_licenses, tag_list: $tag_list, locale: $locale)
  }
`;

// Create a higher-order component to connect the class component to context
const withOnboardingContext = WrappedComponent => {
  return props => {
    const context = useOnboardingState() || {};
    const currentLanguage = context.currentLanguage || props.currentLanguage || 'en';
    console.log('PracticeInformationStep language from context:', currentLanguage);
    return <WrappedComponent {...props} currentLanguage={currentLanguage} onboardingContext={context} />;
  };
};

// Helper functions from AdvisorLicenseComponent
const removeAmpOptions = advisor_licenses => {
  const ampIndex = findIndex(advisor_licenses, o => o === 'AMP');
  if (ampIndex > -1) {
    advisor_licenses.splice(ampIndex, 1);
  }
  const optiondIndex = findIndex(advisor_licenses, o => o === 'Options');
  if (optiondIndex > -1) {
    advisor_licenses.splice(optiondIndex, 1);
  }
  return advisor_licenses;
};

const checkIfIiroc = (iirocLicenseOptions, userAdvisorLicenses, license) => {
  if (iirocLicenseOptions) {
    if (!userAdvisorLicenses.includes('IIROC')) {
      return license === 'AMP' || license === 'Options';
    }
  }
  return false;
};

const alphaSortLicenses = licenses => {
  return [...licenses].sort((a, b) => a.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: 'base',
  }));
};

class PracticeInformationStep extends React.Component {
  constructor(props) {
    super(props);
    
    // Get initial values from props.user or window.__WIZARD_DATA__
    const resumeData = window?.__WIZARD_DATA__?.resumeData || {};
    const user = props.user || {};
    const companySettings = window?.__WIZARD_DATA__?.companySettings || {};
    const businessType = companySettings.business_type || user?.company?.business_type || '';
    
    this.state = {
      // Form data
      primaryMarkets: user.primary_markets || resumeData.primary_markets || [],
      designations: user.designations || resumeData.designations || [],
      advisorLicenses: user.advisor_licenses || resumeData.advisor_licenses || [],
      professionalStrengths: user.tag_list || resumeData.tag_list || [],
      
      // Available options
      availablePrimaryMarkets: props.availablePrimaryMarkets || [],
      availableDesignations: props.availableDesignations || [],
      availableAdvisorLicenses: props.availableAdvisorLicenses || [],
      availableStrengths: props.availableStrengths || [],
      
      // New inputs
      newStrength: '',
      
      // Loading and error states
      loading: false,
      error: null,
      
      // Form validity
      formValid: false,
      
      // Company features
      iirocLicenseOptions: props.iirocLicenseOptions || companySettings.iiroc_licensing_options || false
    };
    
    // Bind methods
    this.togglePrimaryMarket = this.togglePrimaryMarket.bind(this);
    this.toggleDesignation = this.toggleDesignation.bind(this);
    this.toggleAdvisorLicense = this.toggleAdvisorLicense.bind(this);
    this.toggleProfessionalStrength = this.toggleProfessionalStrength.bind(this);
    this.handleAddProfessionalStrength = this.handleAddProfessionalStrength.bind(this);
    this.alphaSortLicenses = this.alphaSortLicenses.bind(this);
    this.savePracticeInfo = this.savePracticeInfo.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.checkFormValidity = this.checkFormValidity.bind(this);
    this.getTranslatedText = this.getTranslatedText.bind(this);
  }
  
  componentDidMount() {
    // Initialize available options from props
    this.updateAvailableOptions();
    
    // Check form validity
    this.checkFormValidity();
  }
  
  componentDidUpdate(prevProps, prevState) {
    // Check if user data changed
    if (this.props.user !== prevProps.user) {
      this.updateFromUserData(this.props.user);
    }
    
    // Check if available options changed in props
    if (
      this.props.availablePrimaryMarkets !== prevProps.availablePrimaryMarkets ||
      this.props.availableDesignations !== prevProps.availableDesignations ||
      this.props.availableAdvisorLicenses !== prevProps.availableAdvisorLicenses
    ) {
      this.updateAvailableOptions();
    }
    
    // Check if form data changed
    if (
      prevState.primaryMarkets !== this.state.primaryMarkets ||
      prevState.designations !== this.state.designations ||
      prevState.advisorLicenses !== this.state.advisorLicenses
    ) {
      this.checkFormValidity();
    }

    // Update context form validity when local formValid state changes
    if (prevState.formValid !== this.state.formValid) {
      const { onboardingContext } = this.props;
      if (onboardingContext && typeof onboardingContext.setStepValid === 'function') {
        onboardingContext.setStepValid(this.state.formValid);
      }
    }
  }
  
  updateFromUserData(userData) {
    if (!userData) return;
    
    const updates = {};
    let hasChanges = false;
    
    // Check for updates to primary markets
    if (userData.primary_markets?.length > 0 && 
        JSON.stringify(userData.primary_markets) !== JSON.stringify(this.state.primaryMarkets)) {
      updates.primaryMarkets = [...userData.primary_markets];
      hasChanges = true;
    }
    
    // Check for updates to designations
    if (userData.designations?.length > 0 && 
        JSON.stringify(userData.designations) !== JSON.stringify(this.state.designations)) {
      updates.designations = [...userData.designations];
      hasChanges = true;
    }
    
    // Check for updates to advisor licenses
    if (userData.advisor_licenses?.length > 0 && 
        JSON.stringify(userData.advisor_licenses) !== JSON.stringify(this.state.advisorLicenses)) {
      updates.advisorLicenses = [...userData.advisor_licenses];
      hasChanges = true;
    }
    
    // Check for updates to professional strengths
    if (userData.tag_list?.length > 0 && 
        JSON.stringify(userData.tag_list) !== JSON.stringify(this.state.professionalStrengths)) {
      updates.professionalStrengths = [...userData.tag_list];
      hasChanges = true;
    }
    
    // Update state if needed
    if (hasChanges) {
      this.setState(updates, () => {
        // Re-check form validity after state update
        this.checkFormValidity();
      });
    }
  }
  
  updateAvailableOptions() {
    // Get options from props
    const { availablePrimaryMarkets, availableDesignations, availableAdvisorLicenses, iirocLicenseOptions, availableStrengths } = this.props;
    
    // Update state with available options
    this.setState({
      availablePrimaryMarkets: availablePrimaryMarkets || this.state.availablePrimaryMarkets || [],
      availableDesignations: availableDesignations || this.state.availableDesignations || [],
      availableAdvisorLicenses: availableAdvisorLicenses || this.state.availableAdvisorLicenses || [],
      availableStrengths: availableStrengths || this.state.availableStrengths || [],
      iirocLicenseOptions: iirocLicenseOptions || this.state.iirocLicenseOptions || false
    }, () => {
      console.log('Updated available options in state:', {
        primaryMarkets: this.state.availablePrimaryMarkets,
        designations: this.state.availableDesignations,
        advisorLicenses: this.state.availableAdvisorLicenses,
        availableStrengths: this.state.availableStrengths
      });
    });
  }
  
  checkFormValidity() {
    const { primaryMarkets, designations, advisorLicenses, professionalStrengths } = this.state;
    const businessType = this.getBusinessType();
    
    let isValid = false;
    
    // For p_and_c business type, professional strengths are now optional
    // Designations are optional for all business types
    if (businessType === 'p_and_c') {
      isValid = true;  // Professional strengths are now optional for p_and_c
    } else {
      isValid = primaryMarkets.length > 0 && advisorLicenses.length > 0;  // No longer require designations
    }
      
    this.setState({ formValid: isValid });
    
    // Update context step validity if available
    const { onboardingContext } = this.props;
    if (onboardingContext && typeof onboardingContext.setStepValid === 'function') {
      onboardingContext.setStepValid(isValid);
    }
    
    return isValid;
  }
  
  togglePrimaryMarket(e) {
    e.preventDefault(); // Prevent default button behavior
    
    const market = e.target.value;
    
    // Find market in the current list of primary markets
    const index = findIndex(this.state.primaryMarkets, m => {
      // Handle both string format and object format
      if (typeof m === 'object') {
        return m.name === market || m.value === market;
      }
      return m === market;
    });
    
    const updatedMarkets = [...this.state.primaryMarkets];
    
    if (index > -1) {
      updatedMarkets.splice(index, 1);
    } else {
      updatedMarkets.push(market);
    }
    
    this.setState({ primaryMarkets: updatedMarkets }, () => {
      // Force check form validity after state update
      this.checkFormValidity();
    });
  }
  
  toggleDesignation(designation) {
    
    const index = findIndex(this.state.designations, d => d === designation);
    const updatedDesignations = [...this.state.designations];
    
    if (index > -1) {
      updatedDesignations.splice(index, 1);
    } else {
      updatedDesignations.push(designation);
    }
    
    this.setState({ designations: updatedDesignations }, () => {
      // Force check form validity after state update
      this.checkFormValidity();
    });
  }
  
  toggleAdvisorLicense(e) {
    e.preventDefault(); // Prevent default button behavior
    
    const license = e.target.value;
    const { advisorLicenses, iirocLicenseOptions } = this.state;
    
    // Copy current advisor licenses
    let updatedLicenses = [...advisorLicenses];
    const index = findIndex(updatedLicenses, l => l === license);
    
    if (index > -1) {
      updatedLicenses.splice(index, 1);
    } else {
      updatedLicenses.push(license);
      
      // Handle IIROC related logic
      if (iirocLicenseOptions && license === 'IIROC') {
        // Nothing special happens when adding IIROC
      }
    }
    
    // If IIROC is being removed, also remove AMP and Options if IIROC licensing is enabled
    if (index > -1 && license === 'IIROC' && iirocLicenseOptions) {
      updatedLicenses = removeAmpOptions(updatedLicenses);
    }
    
    this.setState({ advisorLicenses: updatedLicenses }, () => {
      // Force check form validity after state update
      this.checkFormValidity();
    });
  }
  
  toggleProfessionalStrength(e) {
    e.preventDefault(); // Prevent default button behavior
    
    const strength = e.target.value;
    const strength_key = snakeCase(strength);
    
    // Find strength in the current list of professional strengths
    const index = findIndex(this.state.professionalStrengths, s => {
      return snakeCase(s) === strength_key;
    });
    
    const updatedStrengths = [...this.state.professionalStrengths];
    
    if (index > -1) {
      updatedStrengths.splice(index, 1);
    } else {
      updatedStrengths.push(strength_key);
    }
    
    this.setState({ professionalStrengths: updatedStrengths }, () => {
      // Force check form validity after state update
      this.checkFormValidity();
    });
  }
  
  handleAddProfessionalStrength(e) {
    if ((e.key === 'Enter' || e.key === ' ') && this.state.newStrength.trim()) {
      const strengthValue = snakeCase(this.state.newStrength.trim());
      
      // Add only if not already in the list
      if (!this.state.professionalStrengths.includes(strengthValue)) {
        this.setState({
          professionalStrengths: [...this.state.professionalStrengths, strengthValue],
          newStrength: ''
        }, () => {
          this.checkFormValidity();
        });
      } else {
        this.setState({ newStrength: '' });
      }
      
      e.preventDefault();
    }
  }
  
  alphaSortLicenses(licenses) {
    return [...licenses].sort((a, b) => a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    }));
  }
  
  async savePracticeInfo() {
    // Check form validity first
    if (!this.checkFormValidity()) {
      this.setState({ error: this.getTranslatedText('error.form') });
      return false;
    }
    
    try {
      this.setState({ loading: true, error: null });
      
      const { primaryMarkets, designations, advisorLicenses, professionalStrengths } = this.state;
      const businessType = this.getBusinessType();
      
      // Prepare variables for GraphQL mutation
      const variables = {
        designations: designations,
        locale: this.props.currentLanguage
      };

      // For P&C, professional strengths are optional but still save them if provided
      if (businessType === 'p_and_c') {
        // Always include tag_list for p_and_c, even if empty
        variables.tag_list = professionalStrengths;
        // Empty primary markets to avoid conflicts
        variables.primary_markets = [];
        // For p_and_c, send an empty array to clear any existing licenses
        variables.advisor_licenses = [];
      } else {
        variables.primary_markets = primaryMarkets;
        variables.advisor_licenses = advisorLicenses;
        // Don't update tag_list for non-p_and_c
        variables.tag_list = null;
      }
      
      // Execute the GraphQL mutation
      const response = await this.props.mutate({
        variables: variables
      });
      
      const data = response.data;
      
      if (data?.updateCurrentUser) {
        
        // Prepare user update data
        const userData = {
          designations: designations,
        };
        
        // For P&C, include professional strengths
        if (businessType === 'p_and_c') {
          userData.tag_list = professionalStrengths;
          userData.primary_markets = [];
          userData.advisor_licenses = [];
        } else {
          userData.primary_markets = primaryMarkets;
          userData.advisor_licenses = advisorLicenses;
        }
        
        // Update user in context if onUserUpdate is provided
        if (typeof this.props.onUserUpdate === 'function') {
          this.props.onUserUpdate(userData);
        }
        
        // Also update window.__WIZARD_DATA__ for persistence
        if (window.__WIZARD_DATA__ && window.__WIZARD_DATA__.resumeData) {
          window.__WIZARD_DATA__.resumeData = {
            ...window.__WIZARD_DATA__.resumeData,
            ...userData
          };
        }
        
        // Update context user if available
        const { onboardingContext } = this.props;
        if (onboardingContext && typeof onboardingContext.updateUser === 'function') {
          onboardingContext.updateUser(userData);
        }
        
        this.setState({ loading: false });
        return true;
      } else {
        throw new Error('Failed to update practice information');
      }
    } catch (err) {
      console.error('Error updating practice information:', err);
      this.setState({ 
        error: this.getTranslatedText('error.save'),
        loading: false
      });
      return false;
    }
  }
  
  async handleContinue() {
    try {    
      const saveSuccessful = await this.savePracticeInfo();
      
      if (saveSuccessful) {
        // Prioritize props.onNext (Redux/OnboardingWizard) which saves to server
        if (typeof this.props.onNext === 'function') {
          this.props.onNext();
          return true;
        }
        
        // Fallback to context navigation if props.onNext not available
        const { onboardingContext } = this.props;
        if (onboardingContext && typeof onboardingContext.goToNextStep === 'function') {
          onboardingContext.goToNextStep();
          return true;
        }
        
        // Last resort: direct URL navigation
        window.location.href = '/onboarding/headline_and_about_me';
        return true;
      }
      
      return false;
    } catch (err) {
      console.error("Error in continue button click handler:", err);
      this.setState({ error: this.getTranslatedText('error.navigate') });
      return false;
    }
  }
  
  handleBack() {
    
    // Prefer props navigation (from OnboardingWizard) over context navigation
    // This ensures proper synchronization when used inside OnboardingWizard
    if (typeof this.props.onBack === 'function') {
      this.props.onBack();
      return;
    }
    
    // Try using context navigation as fallback
    const { onboardingContext } = this.props;
    if (onboardingContext && typeof onboardingContext.goToPreviousStep === 'function') {
      onboardingContext.goToPreviousStep();
      return;
    }
    
    // Final fallback to direct URL navigation
    window.location.href = '/onboarding/production_information';
  }
  
  // Get business type from user's company or global settings
  getBusinessType() {
    return this.props.user?.company?.business_type || 
           window.__WIZARD_DATA__?.companySettings?.business_type || 
           'other';
  }

  // Get translated text based on current language
  getTranslatedText(key) {
    const { currentLanguage } = this.props;
    
    // Define translations
    const translations = {
      en: {
        'title': 'Practice Information',
        'subtitle': 'Tell us more about your practice to help us better understand your needs.',
        'primary.markets': 'Primary Markets',
        'primary.markets.desc': 'Select the primary markets you serve.',
        'primary.markets.warning': 'Please select at least one primary market',
        'loading.markets': 'Loading available markets...',
        'professional.strengths': 'Professional Strengths',
        'professional.strengths.desc': 'Add your professional strengths or areas of expertise.',
        'professional.strengths.warning': 'Please add at least one professional strength',
        'professional.strengths.optional': 'No professional strengths added (optional)',
        'professional.strengths.placeholder': 'Type a professional strength and press Enter',
        'designations': 'Designations',
        'designations.desc': 'Select the designations you\'ve earned.',
        'designations.warning': 'No designations selected (optional)',
        'licenses': 'Licenses Held',
        'licenses.desc': 'Select the professional licenses you currently hold.',
        'licenses.warning': 'Please select at least one license',
        'error.form': 'Please fill out all required sections before continuing.',
        'error.save': 'There was a problem saving your information. Please try again.',
        'error.navigate': 'There was a problem navigating to the next step. Please try again.',
        'back': 'Back',
        'continue': 'Continue',
        'saving': 'Saving...'
      },
      fr: {
        'title': 'Informations sur la Pratique',
        'subtitle': 'Parlez-nous davantage de votre pratique pour nous aider à mieux comprendre vos besoins.',
        'primary.markets': 'Marchés Principaux',
        'primary.markets.desc': 'Sélectionnez les marchés principaux que vous servez.',
        'primary.markets.warning': 'Veuillez sélectionner au moins un marché principal',
        'loading.markets': 'Chargement des marchés disponibles...',
        'professional.strengths': 'Forces Professionnelles',
        'professional.strengths.desc': 'Ajoutez vos forces professionnelles ou domaines d\'expertise.',
        'professional.strengths.warning': 'Veuillez ajouter au moins une force professionnelle',
        'professional.strengths.optional': 'Aucune force professionnelle ajoutée (facultatif)',
        'professional.strengths.placeholder': 'Tapez une force professionnelle et appuyez sur Entrée',
        'designations': 'Désignations',
        'designations.desc': 'Sélectionnez les désignations que vous avez obtenues.',
        'designations.warning': 'Aucune désignation sélectionnée (facultatif)',
        'licenses': 'Licences Détenues',
        'licenses.desc': 'Sélectionnez les licences professionnelles que vous détenez actuellement.',
        'licenses.warning': 'Veuillez sélectionner au moins une licence',
        'error.form': 'Veuillez remplir toutes les sections requises avant de continuer.',
        'error.save': 'Un problème est survenu lors de l\'enregistrement de vos informations. Veuillez réessayer.',
        'error.navigate': 'Un problème est survenu lors de la navigation vers l\'étape suivante. Veuillez réessayer.',
        'back': 'Retour',
        'continue': 'Continuer',
        'saving': 'Enregistrement...'
      },
      'en-GB': {
        'title': 'Practice Information',
        'subtitle': 'Tell us more about your practice to help us better understand your needs.',
        'primary.markets': 'Primary Markets',
        'primary.markets.desc': 'Select the primary markets you serve.',
        'primary.markets.warning': 'Please select at least one primary market',
        'loading.markets': 'Loading available markets...',
        'professional.strengths': 'Professional Strengths',
        'professional.strengths.desc': 'Add your professional strengths or areas of expertise.',
        'professional.strengths.warning': 'Please add at least one professional strength',
        'professional.strengths.optional': 'No professional strengths added (optional)',
        'professional.strengths.placeholder': 'Type a professional strength and press Enter',
        'designations': 'Qualifications',
        'designations.desc': 'Select the qualifications you\'ve earned.',
        'designations.warning': 'No qualifications selected (optional)',
        'licenses': 'Advice Areas',
        'licenses.desc': 'Select the advice areas you currently cover.',
        'licenses.warning': 'Please select at least one advice area',
        'error.form': 'Please fill out all required sections before continuing.',
        'error.save': 'There was a problem saving your information. Please try again.',
        'error.navigate': 'There was a problem navigating to the next step. Please try again.',
        'back': 'Back',
        'continue': 'Continue',
        'saving': 'Saving...'
      }
    };
    
    // Return translated text or default to English
    return translations[currentLanguage]?.[key] || translations['en'][key] || key;
  }
  
  render() {
    const { 
      primaryMarkets, 
      designations, 
      advisorLicenses,
      professionalStrengths,
      availablePrimaryMarkets,
      availableDesignations,
      availableAdvisorLicenses,
      availableStrengths,
      newStrength,
      loading,
      error,
      formValid,
      iirocLicenseOptions
    } = this.state;
    
    const mutateLoading = this.props.loading;
    const locale = this.props.user?.locale || 'en';
    const businessType = this.getBusinessType();
    const isPAndC = businessType === 'p_and_c';
    
    // If no primary markets available, show a loading message
    if (!isPAndC && availablePrimaryMarkets.length === 0) {
      console.log('No primary markets available yet, loading...');
    }
    
    return (
      <div className="bg-succession-bg-primary shadow-succession-lg rounded-xl p-8" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
        <h2 className="text-2xl font-medium text-succession-text-primary mb-3 text-center">{this.getTranslatedText('title')}</h2>
        <p className="text-md text-succession-text-secondary mb-8 text-center max-w-2xl mx-auto">
          {this.getTranslatedText('subtitle')}
        </p>
        
        {error && (
          <div className="mb-6 p-4 bg-succession-error-bg border border-red-200 rounded-md">
            <p className="text-sm text-succession-error">{error}</p>
          </div>
        )}
        
        <div className="grid grid-cols-1 gap-8 mb-8">
          {/* Primary Markets or Professional Strengths Section based on business type */}
          {isPAndC ? (
            <div className="space-y-4 border border-succession-border rounded-md p-6 hover:shadow-succession-md transition-shadow duration-200" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
              <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-2">{this.getTranslatedText('professional.strengths')} (Optional)</h3>
              <p className="text-sm text-succession-text-secondary">{this.getTranslatedText('professional.strengths.desc')}</p>
              
              {professionalStrengths.length === 0 && (
                <p className="text-xs text-succession-text-muted bg-succession-bg-secondary rounded-md p-2 flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-succession-text-muted" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {this.getTranslatedText('professional.strengths.optional')}
                </p>
              )}
              
              <div className="mb-4 relative">
                <input
                  type="text"
                  placeholder={this.getTranslatedText('professional.strengths.placeholder')}
                  className="w-full p-2.5 pr-10 border border-succession-border rounded-md focus:ring-succession-primary focus:border-succession-primary shadow-sm transition-colors bg-succession-bg-primary text-succession-text-primary"
                  style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}
                  value={newStrength}
                  onChange={(e) => this.setState({ newStrength: e.target.value })}
                  onKeyDown={this.handleAddProfessionalStrength}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-succession-text-muted">
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2.5 mt-4">
                {/* Show selected strengths */}
                {professionalStrengths.map(strength => (
                  <button
                    key={strength}
                    className="px-3 py-1.5 rounded-full text-sm font-medium bg-succession-primary-10 text-succession-primary border border-succession-primary shadow-sm hover:bg-succession-bg-secondary hover:text-succession-primary transition-all duration-200 flex items-center"
                    onClick={(e) => {
                      e.preventDefault();
                      this.toggleProfessionalStrength(e);
                    }}
                    value={strength}
                  >
                    <svg className="w-3.5 h-3.5 mr-1.5 text-succession-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {capitalize(strength.replace(/_/g, ' '))}
                  </button>
                ))}
                
                {/* Show suggested strengths that haven't been selected */}
                {availableStrengths
                  .filter(strength => !professionalStrengths.includes(snakeCase(strength)))
                  .slice(0, 10) // Limit the number of suggestions shown
                  .map(strength => (
                    <button
                      key={strength}
                      className="px-3 py-1.5 rounded-full text-sm font-medium bg-succession-bg-secondary text-succession-text-secondary border border-succession-border hover:bg-succession-bg-secondary hover:border-succession-primary hover:text-succession-primary transition-all duration-200"
                      onClick={(e) => {
                        e.preventDefault();
                        this.toggleProfessionalStrength(e);
                      }}
                      value={strength}
                    >
                      {capitalize(strength.replace(/_/g, ' '))}
                    </button>
                  ))
                }
              </div>
            </div>
          ) : (
            <div className="space-y-4 border border-succession-border rounded-md p-6 hover:shadow-succession-md transition-shadow duration-200" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
              <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-2">{this.getTranslatedText('primary.markets')}</h3>
              <p className="text-sm text-succession-text-secondary">{this.getTranslatedText('primary.markets.desc')}</p>
              
              {primaryMarkets.length === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded-md p-2 flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {this.getTranslatedText('primary.markets.warning')}
                </p>
              )}
              
              <div className="flex flex-wrap gap-2.5 mt-4">
                {availablePrimaryMarkets.length === 0 ? (
                  <div className="flex items-center justify-center w-full p-8 border border-succession-border rounded-md bg-succession-bg-secondary">
                    <svg className="animate-spin h-5 w-5 text-succession-primary mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm text-succession-text-muted">{this.getTranslatedText('loading.markets')}</span>
                  </div>
                ) : (
                  availablePrimaryMarkets.map(market => {
                    // Handle both string format and object format
                    const marketValue = typeof market === 'object' ? market.name || market.value : market;
                    const marketLabel = typeof market === 'object' ? 
                      (locale === 'fr' ? (market.fr_name || market.name || market.value) : (market.name || market.value)) 
                      : capitalize(market.replace(/_/g, ' '));
                    
                    const isSelected = findIndex(primaryMarkets, m => m === marketValue) > -1;
                    
                    return (
                      <button
                        key={marketValue}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center ${
                          isSelected 
                            ? 'bg-succession-primary-10 text-succession-primary border border-succession-primary shadow-sm hover:bg-succession-bg-secondary hover:text-succession-primary' 
                            : 'bg-succession-bg-secondary text-succession-text-secondary border border-succession-border hover:bg-succession-bg-secondary hover:border-succession-primary hover:text-succession-primary'
                        }`}
                        value={marketValue}
                        onClick={this.togglePrimaryMarket}
                      >
                        {isSelected && (
                          <svg className="w-3.5 h-3.5 mr-1.5 text-succession-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {marketLabel}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
          
          {/* Designations Section */}
          <div className="space-y-4 border border-succession-border rounded-md p-6 hover:shadow-succession-md transition-shadow duration-200" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
            <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-2">{this.getTranslatedText('designations')}</h3>
            <p className="text-sm text-succession-text-secondary">{this.getTranslatedText('designations.desc')}</p>
            
            {designations.length === 0 && (
              <p className="text-xs text-succession-text-muted bg-succession-bg-secondary rounded-md p-2">
                {this.getTranslatedText('designations.warning')}
              </p>
            )}
            
            <div className="flex flex-wrap gap-2.5">
              {availableDesignations.map(designation => {
                const name = typeof designation === 'object' ? designation.name : designation;
                const frName = typeof designation === 'object' ? (designation.fr_name || name) : name;
                const displayName = locale === 'fr' ? frName : name;
                const isSelected = findIndex(designations, d => d === name) > -1;
                
                return (
                  <button
                    key={name}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center ${
                      isSelected 
                        ? 'bg-succession-primary-10 text-succession-primary border border-succession-primary shadow-sm hover:bg-succession-bg-secondary hover:text-succession-primary' 
                        : 'bg-succession-bg-secondary text-succession-text-secondary border border-succession-border hover:bg-succession-bg-secondary hover:border-succession-primary hover:text-succession-primary'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      this.toggleDesignation(name);
                    }}
                  >
                    {isSelected && (
                      <svg className="w-3.5 h-3.5 mr-1.5 text-succession-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    {displayName}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Licenses Held Section - Only show for non-P&C */}
          {!isPAndC && (
            <div className="space-y-4 border border-succession-border rounded-md p-6 hover:shadow-succession-md transition-shadow duration-200" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
              <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-2">{this.getTranslatedText('licenses')}</h3>
              <p className="text-sm text-succession-text-secondary">{this.getTranslatedText('licenses.desc')}</p>
              
              {advisorLicenses.length === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded-md p-2 flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {this.getTranslatedText('licenses.warning')}
                </p>
              )}
              
              <div className="flex flex-wrap gap-2.5 mt-4">
                {this.alphaSortLicenses(availableAdvisorLicenses).map(license => {
                  const isSelected = findIndex(advisorLicenses, l => l === license) > -1;
                  const isDisabled = checkIfIiroc(iirocLicenseOptions, advisorLicenses, license);
                  
                  return (
                    <button
                      key={license}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center ${
                        isDisabled 
                          ? 'bg-succession-bg-secondary text-succession-text-muted border border-succession-border cursor-not-allowed opacity-70'
                          : isSelected
                            ? 'bg-succession-primary-10 text-succession-primary border border-succession-primary shadow-sm hover:bg-succession-bg-secondary hover:text-succession-primary'
                            : 'bg-succession-bg-secondary text-succession-text-secondary border border-succession-border hover:bg-succession-bg-secondary hover:border-succession-primary hover:text-succession-primary'
                      }`}
                      disabled={isDisabled}
                      value={license}
                      onClick={this.toggleAdvisorLicense}
                    >
                      {isSelected && (
                        <svg className="w-3.5 h-3.5 mr-1.5 text-succession-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {license}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between items-center mt-6 pt-6 border-t border-succession-border">
          <button
            type="button"
            onClick={(e) => {
              console.log('PracticeInformationStep - Back button DOM click event triggered');
              e.preventDefault();
              this.handleBack();
            }}
            className="inline-flex justify-center py-2.5 px-6 border border-succession-border text-sm font-medium rounded-md text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-all duration-200"
          >
            <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {this.getTranslatedText('back')}
          </button>
          
          <button 
            onClick={this.handleContinue}
            className={`inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-all duration-200 ${
              loading || mutateLoading || !formValid 
                ? 'bg-succession-primary opacity-60 cursor-not-allowed' 
                : 'bg-succession-primary hover:bg-succession-primary-hover hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary'
            }`}
            disabled={loading || mutateLoading || !formValid}
          >
            {loading || mutateLoading ? (
              <>
                <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {this.getTranslatedText('saving')}
              </>
            ) : (
              <>
                {this.getTranslatedText('continue')}
                <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }
}

// Only wrap with GraphQL for the mutation, expect the data from props
export default withOnboardingContext(
  graphql(UPDATE_CURRENT_USER, {
    name: 'mutate',
    options: { 
      errorPolicy: 'all' 
    }
  })(PracticeInformationStep)
);

// Add PropTypes
PracticeInformationStep.propTypes = {
  user: PropTypes.object,
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  onUserUpdate: PropTypes.func,
  loading: PropTypes.bool,
  mutate: PropTypes.func.isRequired,
  availablePrimaryMarkets: PropTypes.array,
  availableDesignations: PropTypes.array,
  availableAdvisorLicenses: PropTypes.array,
  availableStrengths: PropTypes.array,
  iirocLicenseOptions: PropTypes.bool,
  currentLanguage: PropTypes.string,
  onboardingContext: PropTypes.object
}; 